import React from "react";
import { Button, Flex, Text } from "@jsq/ds/core";
import { TaskType } from "../../types";
import { canViewerActAsAssignee } from "../../utils/taskAssignment";
import { AssignToPicker } from "./AssignToPicker";
import { CheckerActionBar } from "./CheckerActionBar";
import { DefaultActionBar } from "./DefaultActionBar";
import { ExternalActionBar } from "./ExternalActionBar";
import { MakerActionBar } from "./MakerActionBar";
import { OnBehalfActionBar } from "./OnBehalfActionBar";
import * as styles from "./styles_vanilla.css";
import type { TaskGroupOverride } from "./usePostBtsComment";
import type { StageDocumentOptions } from "../../hooks/useDocuments";
import type { ActionConfig, TaskStepDetail } from "../../types";

interface ActionBarProps {
  task: TaskStepDetail;
  actionLoading: boolean;
  allSubtasksCompleted: boolean;
  onActionClick: (action: string, payload?: Record<string, unknown>) => void;
  /**
   * Fired by the left-zone assignment control. `action` is one of:
   *   "claim"    — task is unassigned, current user wants to take it.
   *   "unassign" — task is assigned, current user wants to clear it.
   * Assign-to-someone-else on an UNASSIGNED task goes through the same
   * onActionClick("reassign", payload) contract as the CRLT-484 reassign modal
   * — the left-zone picker just fires it directly instead of via a modal.
   */
  onAssignmentAction: (action: "claim" | "unassign") => void;
  /**
   * Portal flag. The same task detail renders in both the Canopy (FA) and GPX
   * (GP) portals; in the external-review (WAITING) state the buttons differ:
   *   isGPX=false (Canopy/FA) → "Approve on Client's Behalf" (records the GP's
   *                             decision on their behalf, with evidence).
   *   isGPX=true  (GPX/GP)    → "Approve" / "Return to Fund Admin".
   * Defaults to false so existing FA-side callers and tests are unaffected.
   */
  isGPX?: boolean;
  /**
   * workflow_user.id for the logged-in user. Used to derive `isNotAssignee` — true when the task is
   * unassigned OR assigned to a different user — which locks every progression
   * button across the variant bars. Permissive when empty (defer to BE).
   */
  workflowUserId?: string;
  /**
   * Arena id of the workflow — threaded through to `ConfirmSubmitModal` so its
   * "Assign to" dropdown can fetch the real GP user list via
   * `gpUsersForArena`. Sourced from `workflow.clientId` in `Detail/index.tsx`
   * (clientId IS the arenaId per BE contract). When undefined the modal
   * skips the fetch and renders an empty dropdown.
   */
  arenaId?: number;
  /** JSM issue key (workflow.externalRefId) forwarded to CheckerActionBar,
   * OnBehalfActionBar, and ExternalActionBar so notes entered during actions
   * are posted as BTS comments via usePostBtsComment. */
  issueKey?: string;
  stageDocument?: (file: File, options: StageDocumentOptions) => Promise<number>;
  deleteUploadedDocument?: (documentId: number) => Promise<void>;
  /** Next task group for BTS comment tagging on checker approve → GP approval. */
  nextTask?: TaskGroupOverride;
  /**
   * True while a document upload (presign → S3 PUT → confirm) is in flight.
   * Disables Submit / Complete on maker and default action bars so the user
   * cannot close the task before the 3-step upload lifecycle finishes.
   */
  isDocumentUploading?: boolean;
  clientTeamAutoAssignEnabled?: boolean;
}

const MAKER_STATES = new Set<string>(["OPEN", "PENDING", "IN_PROGRESS"]);
const CHECKER_STATE = "IN_REVIEW";
const EXTERNAL_STATE = "WAITING";

function isMakerCheckerTask(task: TaskStepDetail): boolean {
  return task.taskType === TaskType.MAKER_CHECKER;
}

// claim + reassign + unassign are surfaced via the dedicated left-zone button —
// strip them out of what the variant bars receive so they only render the
// workflow-progression buttons (Submit / Mark complete / Approve / etc).
const ASSIGNMENT_ACTIONS = new Set(["claim", "reassign", "unassign", "cancel_assignment"]);

function findAction(actions: ActionConfig[], name: string): ActionConfig | undefined {
  return actions.find((a) => a.action === name);
}

export const ActionBar: React.FC<ActionBarProps> = ({
  task,
  actionLoading,
  allSubtasksCompleted,
  isDocumentUploading = false,
  onActionClick,
  onAssignmentAction,
  isGPX = false,
  workflowUserId = "",
  arenaId,
  issueKey,
  stageDocument,
  deleteUploadedDocument,
  nextTask,
  clientTeamAutoAssignEnabled = false,
}) => {
  const isNotAssignee = !canViewerActAsAssignee(task, workflowUserId);
  const isMakerChecker = isMakerCheckerTask(task);
  const state = task.executionState;
  const isInReview = state === CHECKER_STATE;
  const showMaker = isMakerChecker && MAKER_STATES.has(state);
  // External-review surface (ExecutionState.WAITING): the task has been handed
  // off to the external party. The same task renders in both portals, so isGPX
  // decides which buttons appear (see the prop docs above). EXTERNAL_APPROVAL
  // tasks in pre-WAITING states keep their normal buttons, so gate strictly on
  // the WAITING state rather than the task type.
  const isWaiting = state === EXTERNAL_STATE;

  // BE-driven action flags — do NOT derive from task.assignee directly.
  const claimAction = findAction(task.availableActions, "claim");
  const cancelAction = findAction(task.availableActions, "cancel_assignment");

  // Right zone — progression actions (Submit / Mark complete / Approve / etc).
  // Strip assignment-only actions so the variant bars never render them.
  const variantActions = task.availableActions.filter((a) => !ASSIGNMENT_ACTIONS.has(a.action));
  const hasProgressionActions = variantActions.length > 0;
  const variantTask: TaskStepDetail = { ...task, availableActions: variantActions };

  // MAKER_CHECKER + IN_REVIEW: always render the footer for role transparency.
  const forceShowForRoleContext = isMakerChecker && isInReview;

  if (!forceShowForRoleContext && !claimAction && !cancelAction && !hasProgressionActions) {
    return null;
  }

  const showAssignPicker =
    clientTeamAutoAssignEnabled && !!claimAction && !isGPX && arenaId !== undefined;

  const renderLeftZone = (): React.ReactNode => (
    <Flex alignItems="center" gap="8px" className={styles.assigneeSection}>
      <Text variant="regular-md">Assigned to</Text>
      <Text variant="strong-md">{task.assignee?.displayName ?? "Unassigned"}</Text>
      {showAssignPicker && (
        <AssignToPicker
          arenaId={arenaId}
          disabled={actionLoading}
          onClaimSelf={() => onAssignmentAction("claim")}
          onReassign={(newAssigneeId, newAssigneeDisplayName) =>
            onActionClick("reassign", { newAssigneeId, newAssigneeDisplayName })
          }
        />
      )}
      {claimAction && !showAssignPicker && (
        <Button
          variant="neutral"
          disabled={actionLoading}
          onClick={() => onAssignmentAction("claim")}
          data-testid="action-bar-claim"
        >
          {claimAction.label ?? "Assign to me"}
        </Button>
      )}
      {cancelAction && (
        <Button
          variant="neutral"
          disabled={actionLoading}
          onClick={() => onAssignmentAction("unassign")}
          data-testid="action-bar-cancel-assignment"
        >
          {cancelAction.label ?? "Cancel assignment"}
        </Button>
      )}
    </Flex>
  );

  const renderVariant = (): React.ReactNode => {
    if (!hasProgressionActions) return null;
    if (isWaiting) {
      return isGPX ? (
        <ExternalActionBar
          task={variantTask}
          actionLoading={actionLoading}
          isNotAssignee={isNotAssignee}
          onActionClick={onActionClick}
          issueKey={issueKey}
        />
      ) : (
        <OnBehalfActionBar
          task={variantTask}
          actionLoading={actionLoading}
          onActionClick={onActionClick}
          issueKey={issueKey}
          stageDocument={stageDocument}
          deleteUploadedDocument={deleteUploadedDocument}
        />
      );
    }
    if (isMakerChecker && isInReview) {
      return (
        <CheckerActionBar
          task={variantTask}
          actionLoading={actionLoading}
          isNotAssignee={isNotAssignee}
          onActionClick={onActionClick}
          arenaId={arenaId}
          issueKey={issueKey}
          nextTask={nextTask}
        />
      );
    }
    if (showMaker) {
      return (
        <MakerActionBar
          task={variantTask}
          actionLoading={actionLoading}
          allSubtasksCompleted={allSubtasksCompleted}
          isDocumentUploading={isDocumentUploading}
          isNotAssignee={isNotAssignee}
          onActionClick={onActionClick}
        />
      );
    }
    return (
      <DefaultActionBar
        task={variantTask}
        actionLoading={actionLoading}
        allSubtasksCompleted={allSubtasksCompleted}
        isDocumentUploading={isDocumentUploading}
        isNotAssignee={isNotAssignee}
        onActionClick={onActionClick}
      />
    );
  };

  return (
    <Flex alignItems="center" justifyContent="space-between" className={styles.actionBar}>
      {renderLeftZone()}
      {renderVariant()}
    </Flex>
  );
};

export default ActionBar;
